import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import BottomSheet from '../../ui/BottomSheet';
import { buildRiderAppTripPaymentUrl } from '../../../utils/riderAppPaymentUrl';
import {
  resolvePaymentBreakdown,
  toCents,
  fromCents,
  formatCents,
  percentTipCents,
  editableChargeRows,
} from '../../../utils/tripPaymentSummary';
import { TRIP_PAYMENT_STATE } from '../../../utils/tripPaymentStatus';
import { parseHostedPaymentUrl, hostedUrlRejectionMessage } from '../../../utils/hostedPaymentUrl';
import { buildReceiptHtml, downloadReceiptHtml, receiptFileName } from '../../../utils/receiptDocument';
import PassengerVerificationPanel from './PassengerVerificationPanel';
import { OTP_STEP } from './usePassengerOtpDialogue';

/**
 * Collect Payment sheet — shared by Live Dispatch and Itinerary
 * (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md §"Fare and tip presentation").
 *
 * Purely presentational: the owner (LiveDispatch.js / ItineraryDispatch.js) owns socket
 * traffic, payment state and the mode-specific completion command (COMPM vs the
 * itinerary `complete` action). This component only renders the state it is given and
 * reports the driver's choices.
 *
 * Three ways to settle, deliberately different in what they encode:
 *
 * - **Show RiderApp QR** — encodes ONLY `https://<fleet>.riderapp.net/pay?tripId=<tripId>`.
 *   The passenger's phone opens the RiderApp, which resolves the trip, onboards the
 *   rider if needed, shows fare/tip and opens HBSS itself. No server request is made
 *   here; we simply wait for the trip's settlement push. Allowed while Loaded or after
 *   arrival.
 * - **Continue to secure card page** — the fare/tip are chosen here, then the owner asks
 *   AutoDispatch for one HBSS hosted page for `cardAmount + tip`. Every card field lives
 *   on that page; this sheet never renders or stores card input.
 * - **Collect cash** — the existing cash confirmation. No digital tip, no card fee.
 *
 * Toll / card-fee / existing-gratuity rows render only when the server reports them;
 * a missing row is omitted, never shown as $0.00. All money is integer cents.
 *
 * After settlement the sheet offers View receipt and Text receipt (both served by
 * AutoDispatch), Download receipt, plus the owner's completion action. There is no email.
 * Download is generated locally (utils/receiptDocument) from the values shown here,
 * because the hosted receipt is embedded sandboxed and cross-origin and so cannot be
 * saved or printed through the frame.
 */

export const TIP_PRESETS = [0, 15, 20, 25];

function Row({ label, value, strong = false, testId }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-1.5 ${strong ? 'border-t border-gray-300 mt-1 pt-2.5 text-base font-bold text-gray-900' : 'text-sm text-gray-700'}`}
      data-testid={testId}
    >
      <span>{label}</span>
      <span className={strong ? '' : 'font-semibold text-gray-900'}>{value}</span>
    </div>
  );
}

function WaitingPill({ children }) {
  return (
    <div className="flex items-center justify-center">
      <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-800">
        <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
        {children}
      </span>
    </div>
  );
}

function Notice({ tone = 'info', children }) {
  const cls = tone === 'error'
    ? 'bg-red-50 border-red-200 text-red-800'
    : tone === 'good'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-blue-50 border-blue-200 text-blue-900';
  return (
    <p className={`text-sm rounded-lg border px-3 py-2 ${cls}`} role={tone === 'error' ? 'alert' : undefined}>
      {children}
    </p>
  );
}

function PaymentCollectionSheet({
  open,
  onClose,
  // Identity of the trip being collected. tripId is the AutoDispatch Trip.Id GUID.
  tripId,
  tripNumber,
  distanceText,
  // RiderApp host inputs (fleet code → https://<fleet>.riderapp.net/pay; riderAppUrl only
  // as the approved nonstandard override).
  fleetCode,
  riderAppUrl,
  // Server payment block + price rows for the breakdown; fallbackFare is a last resort.
  payment,
  priceItems,
  fallbackFare,
  /** Resolved server payment status (utils/tripPaymentStatus), live overrides applied. */
  paymentStatus,
  // Fare finalization (metered trips): PRICE sent, waiting for PRICE|Ok.
  fareFinalizing = false,
  fareFinalizeError = null,
  onRetryFinalize,
  // Manual fare entry for a trip whose fare cannot be produced by a meter.
  // Optional: owners without a manual-entry path simply omit it.
  onEnterFareManually = null,
  // Cash
  onCollectCash,
  cashPending = false,
  cashError = null,
  allowCash = true,
  // Hosted card page
  onRequestHostedPage,
  collectionUrl = null,
  collectionError = null,
  /**
   * Re-ask AD whether this trip is actually settled. Shown when a card attempt did not
   * confirm: an abandoned hosted page leaves nothing collected, and a page paid a moment
   * ago may still be in flight, so the driver needs to be able to check rather than
   * guess. Omit to hide the control.
   */
  onCheckPaymentStatus,
  checkingPaymentStatus = false,
  /**
   * Passenger verification dialogue (usePassengerOtpDialogue). When supplied, the
   * card path runs through it first: AutoDispatch texts the passenger a code, the
   * driver enters it, AD assigns that contact to the trip, and only then is the
   * hosted page requested. Omitting it keeps the previous direct-to-card behaviour,
   * so a fleet/server without the dialogue commands is unaffected.
   */
  otpDialogue = null,
  /**
   * Receives the fare/tip the driver chose, at the moment verification starts, so
   * the owner can request collection for exactly those amounts once AD confirms.
   */
  onSelectFare = null,
  /** Set once the passenger has been verified for THIS trip in this session. */
  passengerVerified = false,
  // Receipts
  onViewReceipt,
  onTextReceipt,
  receipt = null, // { loading, url, error, messageSid, sentTo }
  // Identifying detail for a downloaded receipt copy. All optional: a missing row is
  // omitted from the document rather than rendered blank.
  fleetName = '',
  driverName = '',
  // Completion (owner sends COMPM / itinerary complete). Disabled while still Loaded.
  onCompleteTrip,
  canCompleteNow = true,
  completeLabel = 'Complete trip',
  /**
   * Collection-time charge edits, owned by the parent: `{ [Trip_Price.Id]: amountCents }`.
   * `onChangeChargeEdit(id, amountCents)` with a null amount reverts a row to the
   * server's stored value. Nothing here is saved on its own — the edits ride in the
   * collection request and AD applies them only on confirmed settlement.
   */
  chargeEdits = null,
  onChangeChargeEdit = null,
}) {
  const [view, setView] = useState('choose'); // choose | qr | hosted | cash | text-receipt
  const [tipMode, setTipMode] = useState(0); // 0 | 15 | 20 | 25 | 'custom'
  const [customTip, setCustomTip] = useState('');
  const [mobile, setMobile] = useState('');
  const openedCollectionUrlRef = useRef(null);
  const receiptActionRef = useRef(null); // 'view' | 'text'
  // The receipt host sends CSP frame-ancestors limited to the fleet's own domains, so
  // embedding succeeds from a deployed DriverApp but is refused from localhost (and from
  // any host not on that list). A refused frame renders the browser's own error page
  // inside the sheet, so watch for a load that never reports success and show our own
  // fallback instead.
  const [receiptFrameBlocked, setReceiptFrameBlocked] = useState(false);
  // True when the browser refused the automatic card-page pop-up, so the hosted view
  // leads with an explicit "Open" button instead of a passive waiting pill.
  const [popupBlocked, setPopupBlocked] = useState(false);
  // null | 'saved' | 'failed' — a download gives no completion event, so the only honest
  // feedback is whether the browser accepted the save request.
  const [downloadState, setDownloadState] = useState(null);

  const breakdown = useMemo(
    () => resolvePaymentBreakdown({ payment, priceItems, fallbackFare }),
    [payment, priceItems, fallbackFare]
  );
  // The trip's editable charge rows, and the net effect of the driver's edits. AD
  // recalculates authoritatively on settlement; this only keeps the amount on screen
  // honest about what is being submitted.
  const chargeRows = useMemo(() => editableChargeRows(priceItems), [priceItems]);
  const editDeltaCents = useMemo(() => {
    if (!chargeEdits) return 0;
    return chargeRows.reduce((sum, row) => {
      if (!Object.prototype.hasOwnProperty.call(chargeEdits, row.id)) return sum;
      return sum + (Math.max(0, Math.round(Number(chargeEdits[row.id]) || 0)) - row.amountCents);
    }, 0);
  }, [chargeRows, chargeEdits]);
  const canEditCharges = typeof onChangeChargeEdit === 'function' && chargeRows.length > 0;

  // Edits shift the fare, never below zero. `fareAmount` is a MINIMUM the server will
  // accept and may be raised, so an increase needs no extra permission.
  const cardCents = Math.max(0, toCents(breakdown.cardAmount) + editDeltaCents);
  const walletCents = Math.max(0, toCents(breakdown.walletAmount) + editDeltaCents);
  const baseFareCents = Math.max(0, toCents(breakdown.baseFare) + editDeltaCents);
  // Percentage presets apply to the base fare only — not to tolls or the card
  // processing fee — the same basis the RiderApp fare/tip screen uses.
  const tipCents = tipMode === 'custom'
    ? Math.max(0, toCents(customTip))
    : percentTipCents(baseFareCents, Number(tipMode) || 0);
  const totalCents = cardCents + tipCents;

  const qrUrl = useMemo(
    () => buildRiderAppTripPaymentUrl({ fleetCode, riderAppUrl, tripId }),
    [fleetCode, riderAppUrl, tripId]
  );

  const state = paymentStatus?.state;
  const isPaid = state === TRIP_PAYMENT_STATE.PAID || state === TRIP_PAYMENT_STATE.ACCOUNT_BILLED;
  const needsReview = state === TRIP_PAYMENT_STATE.NEEDS_REVIEW;
  const isPending = state === TRIP_PAYMENT_STATE.PENDING;
  const isNoCharge = state === TRIP_PAYMENT_STATE.NO_CHARGE;
  const isNotFinal = state === TRIP_PAYMENT_STATE.FARE_NOT_FINAL;

  // Reset local view state whenever the sheet closes or moves to another trip.
  useEffect(() => {
    if (!open) {
      setView('choose');
      setTipMode(0);
      setCustomTip('');
      setMobile('');
      openedCollectionUrlRef.current = null;
      receiptActionRef.current = null;
      setReceiptFrameBlocked(false);
      setDownloadState(null);
      setPopupBlocked(false);
      // The OTP challenge is bound to this trip and vehicle; a closed sheet must
      // not leave one live for the next trip the driver opens.
      otpDialogue?.reset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tripId]);

  // Verification finished — hand over to the hosted-page view, which is what actually
  // opens the card page when the link arrives. Without this the dialogue would sit on
  // its "Opening the secure card page…" screen forever: the owner requests collection
  // from its onVerified handler, but the URL renders on the 'hosted' view, so that view
  // has to be mounted for the driver to ever reach the page.
  const otpStep = otpDialogue?.step;
  useEffect(() => {
    if (view === 'verify' && otpStep === OTP_STEP.DONE) setView('hosted');
  }, [view, otpStep]);

  // A card page is only ever opened after this check passes. `safeCollection`
  // is the ONLY value allowed to reach window.open or an href below.
  const collectionUrlCheck = useMemo(
    () => (collectionUrl ? parseHostedPaymentUrl(collectionUrl) : null),
    [collectionUrl]
  );
  const safeCollectionUrl = collectionUrlCheck?.ok ? collectionUrlCheck.href : null;
  const collectionUrlError = collectionUrlCheck && !collectionUrlCheck.ok
    ? hostedUrlRejectionMessage(collectionUrlCheck.reason)
    : null;

  // The signed receipt URL comes from the same untrusted path, so it gets the
  // same treatment. A receipt is not a card page, but it is still a link we
  // hand the driver.
  const receiptUrlCheck = useMemo(
    () => (receipt?.url ? parseHostedPaymentUrl(receipt.url) : null),
    [receipt?.url]
  );
  const safeReceiptUrl = receiptUrlCheck?.ok ? receiptUrlCheck.href : null;

  // The hosted page is "the next screen": open it once when the link arrives.
  useEffect(() => {
    // safeCollectionUrl, never collectionUrl: an unvalidated value must not be
    // opened even once, and a refused one is reported in the markup instead.
    if (view !== 'hosted' || !safeCollectionUrl || isPaid) return;
    if (openedCollectionUrlRef.current === safeCollectionUrl) return;
    openedCollectionUrlRef.current = safeCollectionUrl;
    let opened = null;
    try {
      opened = window.open(safeCollectionUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      opened = null;
    }
    // A blocked pop-up usually RETURNS NULL rather than throwing, so the catch alone
    // would silently swallow it and leave the driver on a "waiting" screen with no
    // page and no explanation. This matters much more with verification in front of
    // the request: window.open now fires seconds after the driver's tap — through
    // lookup, SMS and code entry — so it is no longer treated as user-initiated and
    // is blocked by default in most browsers. Record it and prompt for a manual tap,
    // which IS a fresh user gesture and always allowed.
    setPopupBlocked(!opened);
  }, [view, safeCollectionUrl, isPaid]);

  // View receipt: the signed URL is shown inline (see the receipt panel in the paid
  // view) rather than opened in a new tab, so the driver never leaves the sheet.

  // Arm the blocked-frame detector for each receipt URL: a frame refused by CSP never
  // reports a successful load, so if none arrives we fall back to the link.
  useEffect(() => {
    if (!safeReceiptUrl) return undefined;
    setReceiptFrameBlocked(false);
    const timer = setTimeout(() => setReceiptFrameBlocked(true), 2500);
    return () => clearTimeout(timer);
  }, [safeReceiptUrl]);

  const tripLine = [tripNumber ? `Trip #${tripNumber}` : null, distanceText || null]
    .filter(Boolean)
    .join(' · ');

  const closeBtn = 'w-full rounded-xl border border-gray-200 text-gray-700 font-semibold py-3';
  const primaryBtn = 'w-full rounded-xl bg-blue-600 text-white font-semibold py-3 disabled:opacity-50';

  // Each charge line the server priced this trip with, in its own order. Promo/zero rows
  // are kept: a $0.00 line still explains what was applied.
  //
  // Rows already represented by a dedicated row of their own are dropped, or the sheet
  // states the same money twice: the server's `persistedTip` renders as "Existing
  // gratuity", tolls as "Tolls", and the CC fee as "Card processing fee". Leaving the
  // raw Tip row in here was why the breakdown appeared to double-count a gratuity and
  // why the listed charges did not add up to the Fare shown above them.
  const chargeLines = useMemo(() => {
    const rows = Array.isArray(priceItems) ? priceItems : [];
    return rows
      .map((row) => {
        const name = String(row?.name ?? row?.Name ?? '').trim();
        const kind = String(row?.paymentItem ?? row?.PaymentItem ?? '').trim().toLowerCase();
        const cents = toCents(
          row?.TripAmount ?? row?.tripAmount ?? row?.rate ?? row?.Rate ?? row?.Amount ?? row?.amount
        );
        if (!name) return null;
        // Server-tagged rows have their own dedicated line below.
        if (kind === 'tip' || kind === 'toll' || kind === 'tolls' || kind === 'cardsurcharge') return null;
        // Untagged fallback for older payloads that ship no paymentItem tag.
        const normalized = name.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (normalized === 'TIP' || normalized === 'GRATUITY') return null;
        return { name, cents };
      })
      .filter(Boolean);
  }, [priceItems]);

  const fareRows = (withCardFee) => (
    <>
      <Row label="Fare" value={formatCents(baseFareCents)} testId="row-fare" />
      {canEditCharges && (
        <div className="border-t border-gray-200 pt-1.5 mt-1" data-testid="editable-charges">
          <div className="flex items-baseline justify-between pb-1">
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
              Adjust charges
            </span>
            {editDeltaCents !== 0 && (
              <button
                type="button"
                onClick={() => chargeRows.forEach((r) => onChangeChargeEdit(r.id, null))}
                className="text-[11px] font-semibold text-blue-600"
              >
                Reset
              </button>
            )}
          </div>
          {chargeRows.map((row) => {
            const edited = chargeEdits && Object.prototype.hasOwnProperty.call(chargeEdits, row.id);
            const cents = edited ? Math.max(0, Math.round(Number(chargeEdits[row.id]) || 0)) : row.amountCents;
            return (
              <div key={row.id} className="flex items-center justify-between gap-3 py-1">
                <span className="min-w-0 flex-1 truncate text-xs text-gray-600">
                  {row.name || 'Charge'}
                </span>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-400">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    aria-label={`${row.name || 'Charge'} amount`}
                    value={(cents / 100).toFixed(2)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Empty clears the edit rather than charging zero by accident.
                      if (raw === '') return onChangeChargeEdit(row.id, null);
                      const next = toCents(raw);
                      // Back to the server's own amount = no longer an edit, so the
                      // row drops out of the submitted items.
                      onChangeChargeEdit(row.id, next === row.amountCents ? null : next);
                    }}
                    className={`w-20 rounded border px-2 py-1 text-right text-xs font-semibold ${
                      edited ? 'border-blue-400 bg-blue-50 text-blue-900' : 'border-gray-200 text-gray-800'
                    }`}
                  />
                </div>
              </div>
            );
          })}
          <p className="pt-1 text-[10px] leading-snug text-gray-400">
            Saved with the payment — nothing changes unless it settles.
          </p>
        </div>
      )}
      {!canEditCharges && chargeLines.length > 0 && (
        <div className="border-t border-gray-200 pt-1.5 mt-1" data-testid="charge-lines">
          <div className="pb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">
            Trip charges
          </div>
          {chargeLines.map((line, i) => (
            <div
              key={`${line.name}-${i}`}
              className="flex items-center justify-between gap-3 py-1 text-xs text-gray-600"
            >
              <span className="min-w-0 flex-1 truncate">{line.name}</span>
              <span className="font-semibold text-gray-800">{formatCents(line.cents)}</span>
            </div>
          ))}
        </div>
      )}
      {breakdown.hasTolls && <Row label="Tolls" value={formatCents(toCents(breakdown.tolls))} testId="row-tolls" />}
      {withCardFee && breakdown.hasCardSurcharge && (
        <Row label="Card processing fee" value={formatCents(toCents(breakdown.cardSurcharge))} testId="row-card-fee" />
      )}
      {breakdown.hasPersistedTip && (
        <Row label="Existing gratuity" value={formatCents(toCents(breakdown.persistedTip))} testId="row-existing-tip" />
      )}
    </>
  );

  // ── Paid ──────────────────────────────────────────────────────────────────
  if (isPaid) {
    const paidCents = toCents(payment?.paymentAmount);
    const paidAmount = Number.isFinite(Number(payment?.paymentAmount)) && Number(payment.paymentAmount) > 0
      ? formatCents(paidCents)
      : null;

    // The same rows the sheet displays, as plain label/value pairs for the saved copy.
    // Charge lines come first so the detail sits under the fare it explains, matching
    // the on-screen order.
    const receiptLines = [
      { label: 'Fare', value: formatCents(toCents(breakdown.baseFare)) },
      ...chargeLines.map((line) => ({ label: line.name, value: formatCents(line.cents) })),
      breakdown.hasTolls ? { label: 'Tolls', value: formatCents(toCents(breakdown.tolls)) } : null,
      breakdown.hasCardSurcharge
        ? { label: 'CC processing fee', value: formatCents(toCents(breakdown.cardSurcharge)) }
        : null,
      breakdown.hasPersistedTip
        ? { label: 'Tip', value: formatCents(toCents(breakdown.persistedTip)) }
        : null,
    ].filter(Boolean);

    const handleDownloadReceipt = () => {
      const html = buildReceiptHtml({
        fleetName,
        tripNumber,
        paidAt: payment?.paymentSettledAt || payment?.paidAt || undefined,
        driver: driverName,
        paymentMethod: payment?.paymentMethod || payment?.paymentType || '',
        lines: receiptLines,
        // Total paid follows the settled amount when the server reports one, so a
        // downloaded copy can never disagree with what was actually charged.
        total: paidAmount || formatCents(cardCents),
        hostedUrl: safeReceiptUrl || '',
      });
      setDownloadState(downloadReceiptHtml(html, receiptFileName(tripNumber)) ? 'saved' : 'failed');
    };

    if (view === 'text-receipt') {
      const sending = !!receipt?.loading && receiptActionRef.current === 'text';
      const sent = !!receipt?.messageSid && receiptActionRef.current === 'text';
      return (
        <BottomSheet open={open} onClose={onClose} title="Text receipt">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Dispatch texts a private link to the hosted receipt. Enter the passenger's mobile number.
            </p>
            <label className="block text-sm font-semibold text-gray-800">
              Mobile number
              <input
                type="tel"
                inputMode="tel"
                autoComplete="off"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="(212) 555-0100"
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3 text-base font-normal"
                aria-label="Passenger mobile number"
              />
            </label>
            {receipt?.error && receiptActionRef.current === 'text' && <Notice tone="error">{receipt.error}</Notice>}
            {sent && (
              <Notice tone="good">Receipt texted{receipt?.sentTo ? ` to ${receipt.sentTo}` : ''}.</Notice>
            )}
            <button
              type="button"
              className={primaryBtn}
              disabled={sending || !mobile.replace(/\D/g, '').length}
              onClick={() => {
                receiptActionRef.current = 'text';
                onTextReceipt?.(mobile);
              }}
            >
              {sending ? 'Sending…' : sent ? 'Send again' : 'Text receipt'}
            </button>
            <button type="button" className={closeBtn} onClick={() => setView('choose')}>
              Back
            </button>
          </div>
        </BottomSheet>
      );
    }

    const viewing = !!receipt?.loading && receiptActionRef.current === 'view';
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <div className="space-y-4 text-center">
          <div className="text-emerald-600 text-3xl" aria-hidden>✓</div>
          <div>
            <div className="text-lg font-semibold text-gray-900">Payment complete</div>
            {tripLine && <div className="text-xs text-gray-500 mt-1">{tripLine}</div>}
            {paidAmount && <div className="text-2xl font-bold text-gray-900 mt-2">{paidAmount}</div>}
          </div>

          {receipt?.error && receiptActionRef.current === 'view' && <Notice tone="error">{receipt.error}</Notice>}
          {receipt?.messageSid && receiptActionRef.current === 'text' && (
            <Notice tone="good">Receipt texted{receipt?.sentTo ? ` to ${receipt.sentTo}` : ''}.</Notice>
          )}
          {safeReceiptUrl && receiptActionRef.current === 'view' && (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {receiptFrameBlocked ? (
                <p className="px-3 py-4 text-xs text-gray-600">
                  This receipt can&apos;t be shown inside the app from this address. Open it
                  in a new tab, or text it to the passenger.
                </p>
              ) : (
                <iframe
                  src={safeReceiptUrl}
                  title="Payment receipt"
                  data-testid="receipt-frame"
                  className="block h-80 w-full border-0"
                  // The receipt is a hosted document from the payment path: allow it to
                  // render and be scrolled, but never to script against this origin or
                  // navigate the app.
                  sandbox=""
                  referrerPolicy="no-referrer"
                  onLoad={() => setReceiptFrameBlocked(false)}
                />
              )}
              <a
                href={safeReceiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block border-t border-gray-200 px-3 py-2 text-center text-xs font-semibold text-blue-700 underline"
              >
                Open in new tab
              </a>
            </div>
          )}

          <div className="grid gap-2">
            <button
              type="button"
              className={primaryBtn}
              disabled={viewing}
              onClick={() => {
                receiptActionRef.current = 'view';
                onViewReceipt?.();
              }}
            >
              {viewing ? 'Preparing receipt…' : 'View receipt'}
            </button>
            <button
              type="button"
              className="w-full rounded-xl border border-blue-200 bg-blue-50 text-blue-800 font-semibold py-3"
              onClick={() => setView('text-receipt')}
            >
              Text receipt
            </button>
            <button
              type="button"
              className="w-full rounded-xl border border-gray-200 bg-white text-gray-800 font-semibold py-3"
              data-testid="download-receipt"
              onClick={handleDownloadReceipt}
            >
              Download receipt
            </button>
          </div>

          {downloadState === 'saved' && (
            <Notice tone="good">Receipt saved to your device.</Notice>
          )}
          {downloadState === 'failed' && (
            <Notice tone="error">
              This device blocked the download. Use View receipt and save from there.
            </Notice>
          )}

          {canCompleteNow && onCompleteTrip ? (
            <button
              type="button"
              className="w-full rounded-xl bg-emerald-600 text-white font-semibold py-3"
              onClick={onCompleteTrip}
            >
              {completeLabel}
            </button>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                Payment is recorded. Complete the trip at the drop-off as usual.
              </p>
              <button type="button" className={closeBtn} onClick={onClose}>
                Done
              </button>
            </>
          )}
        </div>
      </BottomSheet>
    );
  }

  // ── Needs review / reconciliation ──────────────────────────────────────────
  if (needsReview) {
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <div className="space-y-4">
          {tripLine && <div className="text-center text-xs text-gray-500">{tripLine}</div>}
          <Notice tone="error">
            A payment arrived for this trip but dispatch could not reconcile it. Do not collect
            again and do not complete the trip yet — contact dispatch or support.
          </Notice>
          <button type="button" className={closeBtn} onClick={onClose}>Close</button>
        </div>
      </BottomSheet>
    );
  }

  // ── Fare finalization (PRICE → PRICE|Ok) ──────────────────────────────────
  if (fareFinalizing || fareFinalizeError) {
    return (
      <BottomSheet open={open} onClose={fareFinalizing ? undefined : onClose} title="Collect payment">
        <div className="space-y-4 text-center">
          {tripLine && <div className="text-xs text-gray-500">{tripLine}</div>}
          {fareFinalizing ? (
            <>
              <WaitingPill>Finalizing fare with dispatch…</WaitingPill>
              <p className="text-sm text-gray-600">
                Payment options appear once dispatch confirms the final fare.
              </p>
            </>
          ) : (
            <>
              <Notice tone="error">{fareFinalizeError}</Notice>
              <button type="button" className={primaryBtn} onClick={onRetryFinalize}>
                Retry fare confirmation
              </button>
              <button type="button" className={closeBtn} onClick={onClose}>Cancel</button>
            </>
          )}
        </div>
      </BottomSheet>
    );
  }

  // ── Fare not final (a flag trip before its drop-off price, or a metered trip
  //    whose PRICE has not been acknowledged) ──────────────────────────────────
  // Only the CARD page truly requires a final fare — requestTripCollection is
  // rejected with PRICE_NOT_FINAL. The QR is just a lookup code carrying the trip
  // id: it moves no money and asks the server for nothing, and the RiderApp shows
  // "fare will be finalized at drop-off" and withholds its own Pay button. Showing
  // it early is how a passenger scans and onboards while the trip is still running,
  // which is exactly what the Actions menu offers it for. Cash keeps its legacy
  // close-out. Blocking all three here contradicted that menu and dead-ended the driver.
  if (isNotFinal && view !== 'cash' && view !== 'qr') {
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <div className="space-y-4">
          {tripLine && <div className="text-center text-xs text-gray-500">{tripLine}</div>}
          <Notice>
            This trip's fare is not final yet. The passenger can scan the QR now to open the
            RiderApp, but they can only pay once you confirm the fare at the drop-off.
          </Notice>
          {onEnterFareManually && (
            <button
              type="button"
              onClick={onEnterFareManually}
              className="w-full rounded-xl bg-blue-600 text-white font-semibold py-3 text-left px-4"
            >
              Enter fare
              <span className="block text-xs font-normal text-blue-100">
                Confirm the amount with dispatch to unlock card and QR payment
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setView('qr')}
            className="w-full rounded-xl border border-blue-200 bg-blue-50 text-blue-800 font-semibold py-3 text-left px-4"
          >
            Show RiderApp QR
            <span className="block text-xs font-normal text-blue-700">
              Passenger can scan and sign in now, and pay after the fare is confirmed
            </span>
          </button>
          {allowCash && onCollectCash && (
            <button
              type="button"
              className="w-full rounded-xl bg-emerald-600 text-white font-semibold py-3"
              onClick={() => setView('cash')}
            >
              Collect cash anyway
            </button>
          )}
          <button type="button" className={closeBtn} onClick={onClose}>Close</button>
        </div>
      </BottomSheet>
    );
  }

  // ── No charge ──────────────────────────────────────────────────────────────
  if (isNoCharge) {
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <div className="space-y-4 text-center">
          {tripLine && <div className="text-xs text-gray-500">{tripLine}</div>}
          <div className="text-lg font-semibold text-gray-900">No charge</div>
          <p className="text-sm text-gray-600">Nothing is owed for this trip.</p>
          {canCompleteNow && onCompleteTrip ? (
            <button type="button" className="w-full rounded-xl bg-emerald-600 text-white font-semibold py-3" onClick={onCompleteTrip}>
              {completeLabel}
            </button>
          ) : (
            <button type="button" className={closeBtn} onClick={onClose}>Done</button>
          )}
        </div>
      </BottomSheet>
    );
  }

  // ── Payment pending elsewhere (hosted page / RiderApp reconciliation) ─────
  if (isPending && view !== 'hosted' && view !== 'qr') {
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <div className="space-y-4 text-center">
          {tripLine && <div className="text-xs text-gray-500">{tripLine}</div>}
          <WaitingPill>Payment in progress</WaitingPill>
          <p className="text-sm text-gray-600">
            A payment for this trip is being confirmed. Wait for it to settle before
            collecting again or completing the trip.
          </p>
          <button type="button" className={closeBtn} onClick={onClose}>Close</button>
        </div>
      </BottomSheet>
    );
  }

  // ── QR waiting state ──────────────────────────────────────────────────────
  if (view === 'qr') {
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <div className="space-y-4 text-center">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">RiderApp QR</div>
            {tripLine && <div className="text-xs text-gray-500 mt-0.5">{tripLine}</div>}
          </div>

          {!qrUrl ? (
            <Notice tone="error">
              {!tripId
                ? 'This trip has no server trip id yet — reload the trip before showing a QR.'
                : 'The RiderApp address for this fleet is not known (no fleet code). Sign in again to load fleet settings.'}
            </Notice>
          ) : (
            <>
              <div className="flex justify-center py-1">
                <div className="rounded-2xl bg-white p-4 shadow-sm">
                  <QRCodeSVG value={qrUrl} size={220} />
                </div>
              </div>
              <p className="text-sm font-semibold text-gray-800">Ask the passenger to scan and pay on their phone</p>
              <code
                data-testid="qr-url"
                className="block w-full break-all rounded-lg bg-gray-100 px-3 py-2 text-[11px] text-gray-600"
              >
                {qrUrl}
              </code>
              <div className="rounded-xl border border-gray-200 bg-white px-3 text-left">
                {fareRows(false)}
              </div>
              {isNotFinal ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  The fare is not confirmed yet, so the RiderApp will ask the passenger to wait
                  until you finalize it at the drop-off. They can still scan now to sign in.
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  The passenger chooses card or wallet and adds any tip in the RiderApp.
                  {breakdown.hasCardSurcharge ? ' The card processing fee is shown there when it applies.' : ''}
                </p>
              )}
              <WaitingPill>{isNotFinal ? 'Waiting for the final fare' : 'Waiting for payment'}</WaitingPill>
            </>
          )}

          <button type="button" className={closeBtn} onClick={() => setView('choose')}>
            Back
          </button>
        </div>
      </BottomSheet>
    );
  }

  // ── Passenger verification (assisted card collection) ─────────────────────
  // The passenger must be identified before AD will attach a payment to a contact,
  // so this sits between the fare/tip choice and the hosted page.
  if (view === 'verify' && otpDialogue) {
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <PassengerVerificationPanel
          dialogue={otpDialogue}
          tripLine={tripLine}
          onCancel={() => setView('choose')}
        />
      </BottomSheet>
    );
  }

  // ── Hosted card page waiting state ────────────────────────────────────────
  if (view === 'hosted') {
    const request = () => onRequestHostedPage?.({
      fareAmount: fromCents(cardCents),
      tipAmount: fromCents(tipCents),
      totalAmount: fromCents(totalCents),
    });
    return (
      <BottomSheet open={open} onClose={onClose} title="Collect payment">
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Secure card page</div>
            {tripLine && <div className="text-xs text-gray-500 mt-0.5">{tripLine}</div>}
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3">
            {fareRows(true)}
            {tipCents > 0 && <Row label="Tip" value={formatCents(tipCents)} testId="row-tip" />}
            <Row label="Total Fare" value={formatCents(totalCents)} strong testId="row-total" />
          </div>

          {collectionError ? (
            <>
              <Notice tone="error">{collectionError}</Notice>
              {/* The payment may have gone through after all (a webhook still in
                  flight), so checking comes before recreating a second charge. */}
              {onCheckPaymentStatus && (
                <button
                  type="button"
                  className={primaryBtn}
                  disabled={checkingPaymentStatus}
                  onClick={() => onCheckPaymentStatus()}
                >
                  {checkingPaymentStatus ? 'Checking…' : 'Check payment status'}
                </button>
              )}
              <button type="button" className={closeBtn} onClick={request}>
                Try again
              </button>
              {allowCash && onCollectCash && (
                <button type="button" className={closeBtn} onClick={() => setView('cash')}>
                  Collect cash instead
                </button>
              )}
            </>
          ) : collectionUrlError ? (
            // Refused by validation. Never render a link to it — say why, and
            // give the driver the cash route instead.
            <Notice tone="error">{collectionUrlError}</Notice>
          ) : !safeCollectionUrl ? (
            <WaitingPill>Requesting secure card page…</WaitingPill>
          ) : (
            <>
              {popupBlocked && (
                // The tap on this link is a fresh user gesture, so it is never blocked.
                <Notice>
                  Your browser blocked the payment window. Tap below to open it.
                </Notice>
              )}
              <a
                href={safeCollectionUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setPopupBlocked(false)}
                className="block w-full rounded-xl bg-blue-600 text-center text-white font-semibold py-3"
              >
                Open secure card page
              </a>
              <p className="text-center text-xs text-gray-500">
                Card details are entered only on the secure page at{' '}
                <span className="font-semibold">{collectionUrlCheck.host}</span>. This screen
                updates when the payment settles.
              </p>
              {/* Always shown: the payment is genuinely still outstanding whether or
                  not the pop-up opened, and the notice above already explains a block. */}
              <WaitingPill>Waiting for payment</WaitingPill>
            </>
          )}

          <button type="button" className={closeBtn} onClick={() => setView('choose')}>
            Back
          </button>
        </div>
      </BottomSheet>
    );
  }

  // ── Cash confirmation ─────────────────────────────────────────────────────
  if (view === 'cash') {
    return (
      <BottomSheet open={open} onClose={cashPending ? undefined : onClose} title="Collect payment">
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Cash</div>
            {tripLine && <div className="text-xs text-gray-500 mt-0.5">{tripLine}</div>}
            <div className="text-xs text-gray-500 mt-3">Cash due</div>
            <div className="text-3xl font-bold text-gray-900" data-testid="cash-due">
              {walletCents > 0 ? formatCents(walletCents) : 'Per dispatch'}
            </div>
          </div>
          {walletCents > 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white px-3">
              {fareRows(false)}
            </div>
          ) : (
            <Notice>
              The fare is not finalized on this device; dispatch records the trip's current
              fare for this cash payment.
            </Notice>
          )}
          <p className="text-xs text-gray-500 text-center">
            No card processing fee applies to cash. Handle any cash tip directly with the passenger.
          </p>
          {cashError && <Notice tone="error">{cashError}</Notice>}
          <button
            type="button"
            className="w-full rounded-xl bg-emerald-600 text-white font-semibold py-3 disabled:opacity-50"
            disabled={cashPending}
            onClick={() => onCollectCash?.({ amount: fromCents(walletCents) })}
          >
            {cashPending ? 'Recording cash…' : 'Confirm cash received'}
          </button>
          <button type="button" className={closeBtn} disabled={cashPending} onClick={() => setView('choose')}>
            Back
          </button>
        </div>
      </BottomSheet>
    );
  }

  // ── Choose method (fare + tip first) ──────────────────────────────────────
  const customCentsInvalid = tipMode === 'custom' && customTip !== '' && toCents(customTip) < 0;
  return (
    <BottomSheet open={open} onClose={onClose} title="Collect payment">
      <div className="space-y-4">
        {tripLine && <div className="text-center text-xs text-gray-500">{tripLine}</div>}

        {!breakdown.hasAmount && (
          <Notice>Waiting for the fare from dispatch…</Notice>
        )}

        <div className="rounded-xl border border-gray-200 bg-white px-3">
          {fareRows(true)}
          {tipCents > 0 && <Row label="Tip" value={formatCents(tipCents)} testId="row-tip" />}
          <Row label="Total Fare" value={formatCents(totalCents)} strong testId="row-total" />
        </div>

        <div>
          <div className="text-sm font-semibold text-gray-800 mb-2">Add a tip? <span className="font-normal text-gray-500">(card payments)</span></div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Tip">
            {TIP_PRESETS.map((pct) => {
              const active = tipMode === pct;
              return (
                <button
                  key={pct}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTipMode(pct)}
                  className={`flex-1 min-w-[64px] rounded-lg border px-2 py-2 text-xs font-semibold ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'}`}
                >
                  {pct === 0 ? 'No tip' : `${pct}%`}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={tipMode === 'custom'}
              onClick={() => setTipMode('custom')}
              className={`flex-1 min-w-[64px] rounded-lg border px-2 py-2 text-xs font-semibold ${tipMode === 'custom' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'}`}
            >
              Custom
            </button>
          </div>
          {tipMode === 'custom' && (
            <label className="mt-2 block text-xs font-semibold text-gray-700">
              Custom tip ($)
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={customTip}
                onChange={(e) => setCustomTip(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-normal"
                aria-label="Custom tip amount"
              />
            </label>
          )}
        </div>

        {collectionError && <Notice tone="error">{collectionError}</Notice>}

        <div className="w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="text-sm font-semibold text-blue-800">RiderApp QR</div>
          <div className="text-xs font-normal text-blue-700">
            Passenger scans and pays on their own phone
          </div>
          {!qrUrl ? (
            <p className="mt-2 text-xs text-blue-900" role="alert">
              {!tripId
                ? 'This trip has no server trip id yet — reload the trip to show a QR.'
                : 'The RiderApp address for this fleet is not known (no fleet code). Sign in again to load fleet settings.'}
            </p>
          ) : (
            <>
              <div className="mt-2 flex justify-center">
                <div className="rounded-2xl bg-white p-3 shadow-sm">
                  <QRCodeSVG value={qrUrl} size={160} />
                </div>
              </div>
              <code
                data-testid="qr-url"
                className="mt-2 block w-full break-all rounded-lg bg-white/70 px-3 py-2 text-[11px] text-gray-600"
              >
                {qrUrl}
              </code>
              {isNotFinal && (
                <p className="mt-2 text-xs text-amber-800">
                  The fare is not confirmed yet, so the RiderApp asks the passenger to wait
                  until you finalize it. They can still scan now to sign in.
                </p>
              )}
            </>
          )}
        </div>

        <button
          type="button"
          disabled={!breakdown.hasAmount || cardCents <= 0 || customCentsInvalid}
          onClick={() => {
            // Fare and tip are settled here, then the passenger is verified. The
            // owner requests the page from its onVerified handler, so the amounts
            // shown on this screen are the ones collected.
            if (otpDialogue && !passengerVerified) {
              onSelectFare?.({
                fareAmount: fromCents(cardCents),
                tipAmount: fromCents(tipCents),
                totalAmount: fromCents(totalCents),
              });
              setView('verify');
              return;
            }
            setView('hosted');
            onRequestHostedPage?.({
              fareAmount: fromCents(cardCents),
              tipAmount: fromCents(tipCents),
              totalAmount: fromCents(totalCents),
            });
          }}
          className="w-full rounded-xl bg-blue-600 text-white font-semibold py-3 text-left px-4 disabled:opacity-50"
        >
          Continue to secure card page
          <span className="block text-xs font-normal text-blue-100">
            Card details are entered on the secure page, never here
          </span>
        </button>

        {allowCash && (
          <button
            type="button"
            disabled={!breakdown.hasAmount}
            onClick={() => setView('cash')}
            className="w-full rounded-xl bg-emerald-600 text-white font-semibold py-3 disabled:opacity-50"
          >
            Collect cash
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

export default PaymentCollectionSheet;
